import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCallIntelligenceV2Service, serviceName } from '../service';
import { GetCallsViewByLocationIO, ServiceQueries } from '../types';
import { useCallIntelligenceV2Queries } from './use-call-intelligence-v2-queries';
import { UseCallIntelligenceV2QueryEndpointArgs, useCallIntelligenceV2Query } from './use-call-intelligence-v2-query';

/**
 * A hook that returns a query for the `GetCallsViewByLocation` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetCallsViewByLocationQuery = <E = unknown, D = GetCallsViewByLocationIO['output']>(
  args: UseCallIntelligenceV2QueryEndpointArgs<'GetCallsViewByLocation', E, D>
) =>
  useCallIntelligenceV2Query({
    endpointName: 'GetCallsViewByLocation',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetCallsViewByLocation` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetCallsViewByLocationQueries = <E = unknown, D = GetCallsViewByLocationIO['output']>(
  argsArr: UseCallIntelligenceV2QueryEndpointArgs<'GetCallsViewByLocation', E, D>[]
) =>
  useCallIntelligenceV2Queries(
    argsArr.map((args) => ({
      endpointName: 'GetCallsViewByLocation',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetCallsViewByLocation` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetCallsViewByLocation = () => {
  const queryClient = useQueryClient();

  const fetchGetCallsViewByLocation = async <E = unknown, D = GetCallsViewByLocationIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCallIntelligenceV2QueryEndpointArgs<'GetCallsViewByLocation', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetCallsViewByLocationIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetCallsViewByLocation'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetCallsViewByLocationIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetCallsViewByLocation'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetCallsViewByLocation'>({
          serviceName,
          endpointName: 'GetCallsViewByLocation',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCallIntelligenceV2Service.GetCallsViewByLocation(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetCallsViewByLocation;
};
